import { DisplayConfig, JsxgraphBehaviorConfig, LabelConfig, WidgetConfig } from '@examdojo/core/widget';
import type { ThemeColorName } from '@examdojo/styles';

// ------------------------------------------------------------------
// 1. PARAMS CONFIGURATION
// ------------------------------------------------------------------

export interface QuadraticParams {
  a?: number;
  b?: number;
  c?: number;
}

// ------------------------------------------------------------------
// 2. DISPLAY CONFIGURATION
// ------------------------------------------------------------------

/**
 * Unified configuration for points/features (vertex, roots, etc.)
 * Allows toggling visibility and configuring labels and colors in one place.
 */
export interface QuadraticFeatureConfig {
  enabled?: boolean;
  label?: LabelConfig;
  color?: ThemeColorName;
}

/** Style options for the parabola curve */
export interface ParabolaStyle {
  strokeWidth?: number;
  dashed?: boolean;
}

export interface QuadraticDisplayConfig extends DisplayConfig {
  // Features can now be a simple boolean OR a config object
  vertex?: boolean | QuadraticFeatureConfig;
  roots?: boolean | QuadraticFeatureConfig;
  axisOfSymmetry?: boolean | QuadraticFeatureConfig;
  yIntercept?: boolean | QuadraticFeatureConfig;
  parabola?: boolean | ParabolaStyle;
}

// ------------------------------------------------------------------
// 3. BEHAVIOR CONFIGURATION
// ------------------------------------------------------------------

/**
 * Quadratic-specific action targets.
 */
export type QuadraticActionTarget = 'canvas' | 'parabola' | 'vertex' | 'roots' | 'yIntercept' | 'axisOfSymmetry';

export type QuadraticActionType = 'UPDATE_COEFFICIENTS' | 'FIT_TO_PARABOLA';

/**
 * Quadratic-specific behavior configuration.
 * Extends JsxgraphBehaviorConfig with typed event selectors.
 */
export type QuadraticBehaviorConfig = JsxgraphBehaviorConfig<QuadraticActionTarget, QuadraticActionType>;

// ------------------------------------------------------------------
// 4. MASTER WIDGET CONFIG
// ------------------------------------------------------------------

export type QuadraticWidgetConfig = WidgetConfig<
  'quadratic-function',
  QuadraticParams,
  QuadraticDisplayConfig,
  QuadraticBehaviorConfig
>;

// ------------------------------------------------------------------
// 5. DEFAULTS
// ------------------------------------------------------------------

export const DEFAULT_QUADRATIC_PARAMS: Required<QuadraticParams> = {
  a: 1,
  b: 0,
  c: 0,
};

export const DEFAULT_FEATURE_COLORS: Record<'vertex' | 'roots' | 'axisOfSymmetry' | 'yIntercept', ThemeColorName> = {
  vertex: 'teal',
  roots: 'indigo',
  axisOfSymmetry: 'teal',
  yIntercept: 'amber',
};
